/**
 * GenViz / SmartFence — Types de base du contrat.
 *
 * Rappel des deux règles structurantes (docs/06-revue-critique.md) :
 *  1. Aucune chaîne d'affichage, aucun libellé traduit, aucun identifiant d'écran ne transite
 *     par l'API : seulement des énumérés, des horodatages ISO 8601 UTC et des valeurs numériques
 *     accompagnées de leur unité dans le nom du champ (`_kv`, `_pct`, `_min`, `_s`, `_m`).
 *  2. L'identité d'un objet ne vient jamais de sa charge utile, toujours de son certificat client.
 */

/** Identifiant UUID (v7 pour les entités métier). Sérialisé en chaîne canonique minuscule. */
export type Uuid = string;

/** Horodatage ISO 8601 en UTC, précision milliseconde : `2026-07-30T09:12:00.000Z`. */
export type IsoDateTime = string;

/** Date civile ISO 8601 : `2026-07-30`. */
export type IsoDate = string;

/** Heure locale `HH:mm` (plages de silence des notifications). */
export type HeureHhMm = string;

/** Identifiant de fuseau horaire IANA : `Europe/Paris`. */
export type FuseauHoraireIana = string;

/** Curseur d'une pagination par curseur opaque. `null` = fin de liste. */
export type Curseur = string;

/* -------------------------------------------------------------------------- */
/* Unités — le nom du type documente l'unité, la valeur reste un nombre        */
/* -------------------------------------------------------------------------- */

/** Tension en kilovolts. */
export type Kilovolts = number;
/** Tension en volts (stockage batterie côté backend). */
export type Volts = number;
/** Pourcentage 0..100. */
export type Pourcentage = number;
/** Durée en secondes. */
export type Secondes = number;
/** Durée en minutes. */
export type Minutes = number;
/** Distance en mètres. */
export type Metres = number;
/** Cap en degrés (0 = nord, sens horaire). */
export type Degres = number;
/** Puissance de réception radio en dBm. */
export type Dbm = number;
/** Latitude WGS84, -90..90. */
export type Latitude = number;
/** Longitude WGS84, -180..180. */
export type Longitude = number;

/* -------------------------------------------------------------------------- */
/* Géométrie                                                                   */
/* -------------------------------------------------------------------------- */

/** Point géographique WGS84. */
export interface PointGeo {
  lat: Latitude;
  lon: Longitude;
}

/** Point géographique horodaté. */
export interface PointGeoHorodate extends PointGeo {
  at: IsoDateTime;
}

/** Emprise rectangulaire servant au cadrage automatique de la carte (écran 02A). */
export interface EmpriseGeo {
  nord: Latitude;
  sud: Latitude;
  est: Longitude;
  ouest: Longitude;
}

/* -------------------------------------------------------------------------- */
/* Pagination                                                                  */
/* -------------------------------------------------------------------------- */

/** Paramètres de requête communs à toutes les listes paginées par curseur. */
export interface RequetePaginee {
  /** Curseur opaque renvoyé par l'appel précédent. */
  curseur?: Curseur;
  /** Taille de page demandée. Le serveur peut renvoyer moins. */
  limite?: number;
}

/** Enveloppe de réponse commune à toutes les listes paginées. */
export interface ReponsePaginee<T> {
  items: T[];
  /** Nombre total d'éléments correspondant au filtre, si connu sans coût excessif. */
  total?: number;
  /** Curseur de la page suivante, `null` en fin de liste. */
  curseur_suivant: Curseur | null;
}

/* -------------------------------------------------------------------------- */
/* Concurrence et idempotence                                                  */
/* -------------------------------------------------------------------------- */

/** Noms des en-têtes HTTP utilisés par le contrat. */
export const EnteteHttp = {
  /** Clé d'idempotence, obligatoire sur les créations et les commandes. */
  IDEMPOTENCE: 'Idempotency-Key',
  /** Verrou optimiste : ETag courant renvoyé par le GET correspondant. */
  IF_MATCH: 'If-Match',
  /** ETag renvoyé par les ressources versionnées (paramétrage appareil, nom d'appareil). */
  ETAG: 'ETag',
  /** Langue préférée, utilisée uniquement pour les e-mails générés côté serveur. */
  LANGUE: 'Accept-Language',
  /** Identifiant de corrélation propagé jusqu'aux journaux serveur. */
  TRACE: 'X-Request-Id',
  /** Version de l'application appelante, au format `1.4.2 (137)`. */
  VERSION_APP: 'X-App-Version',
} as const;
export type EnteteHttp = (typeof EnteteHttp)[keyof typeof EnteteHttp];

/** Ressource porteuse d'un verrou optimiste. */
export interface RessourceVersionnee {
  /** Numéro de version incrémenté à chaque écriture. Repris dans l'ETag. */
  version: number;
}

/* -------------------------------------------------------------------------- */
/* Fragments réutilisés dans plusieurs réponses                                */
/* -------------------------------------------------------------------------- */

/** Référence minimale d'une exploitation. */
export interface RefExploitation {
  id: Uuid;
  nom: string;
}

/** Référence minimale d'un électrificateur. */
export interface RefElectrificateur {
  id: Uuid;
  nom: string | null;
}

/** Référence minimale d'un utilisateur (listes, émetteur/destinataire de transfert). */
export interface RefUtilisateur {
  id: Uuid;
  prenom: string;
  nom: string;
  email: string;
}

/** Constantes du contrat REST. */
export const PREFIXE_API_PUBLIQUE = '/v1' as const;
export const PREFIXE_API_INGESTION = '/ingest/v1' as const;
export const PREFIXE_API_INTERNE = '/internal/v1' as const;

/**
 * Cadence de sondage conseillée pendant une session de mesure (écran 04B).
 * La revue critique supprime SSE et WebSocket de la V1 : le polling est le mécanisme nominal.
 */
// 250 ms depuis le « Suivi en direct » (demande GenViz du 25/09/2026) : 80 requêtes pour une
// session de 20 s — sous la limite nginx de 20 req/s par adresse (rafale de 40).
export const SONDAGE_SESSION_MESURE_MS = 250 as const;
